import { inject, Injectable, InjectionToken, Injector } from '@angular/core';
import { type Translation, type TranslocoLoader, TranslocoService } from '@jsverse/transloco';
import { HttpClient } from '@angular/common/http';
import { catchError, forkJoin, map, type Observable, of } from 'rxjs';
import { isString } from '@pct-ts/typeguard';
import * as deepmerge from 'deepmerge';

export const TRANSLOCO_LOADER_LOAD_EXTRA_NAMESPACE = new InjectionToken<string[]>(
  'TRANSLOCO_LOADER_LOAD_EXTRA_NAMESPACE',
);

@Injectable({ providedIn: 'root' })
export class TranslocoHttpLoader implements TranslocoLoader {
  readonly #injector = inject(Injector);
  readonly #http = inject(HttpClient);
  readonly #loadExtraNamespace = inject(TRANSLOCO_LOADER_LOAD_EXTRA_NAMESPACE, { optional: true });

  getTranslation(lang: string): Observable<Translation> {
    const translocoService = this.#injector.get(TranslocoService);
    const langLoader$ = this.#http.get<Translation>(
      `/i18n/${lang}.json?${window.crypto.getRandomValues(new Uint32Array(1))[0]}`,
    );
    if (!Array.isArray(this.#loadExtraNamespace)) {
      return langLoader$;
    }

    // Csak az app fo translate fajlt figyeljuk
    const availableLangs = translocoService.getAvailableLangs();
    if (
      availableLangs.some(
        availableLang => (isString(availableLang) ? availableLang : availableLang.id) === lang,
      )
    ) {
      return forkJoin([
        ...this.#loadExtraNamespace.map(namespace =>
          this.#http
            .get<Translation>(
              `/i18n/${namespace}/${lang}.json?${window.crypto.getRandomValues(new Uint32Array(1))[0]}`,
            )
            .pipe(catchError(() => of({}))),
        ),
        langLoader$,
      ]).pipe(map(results => deepmerge.all(results)));
    }

    return langLoader$;
  }
}
